import { useEffect, useRef, useCallback } from "react"
import type { Card, Phase, SidePot, HandRank } from "@/lib/pokerEngine"
import type { PublicRoomState, ShowdownResultPublic } from "@/lib/pokerBroadcast"

// ---- Event types from server ----

export type PokerWSEvent =
  | { type: "room_update"; room: PublicRoomState }
  | { type: "auto_start_countdown"; seconds: number }
  | { type: "auto_start_cancelled" }
  | { type: "game_start"; handNumber: number; dealerIndex: number }
  | { type: "deal_hole_cards"; cards: Card[] }
  | { type: "player_action"; userId: string; action: string; amount: number }
  | { type: "community_cards"; cards: Card[]; phase: Phase }
  | { type: "showdown"; results: ShowdownResultPublic[] }
  | { type: "pot_update"; pot: number; sidePots: SidePot[] }
  | { type: "turn_change"; userId: string; timeLimit: number }
  | { type: "game_end"; winners: { userId: string; amount: number }[] }
  | { type: "error"; message: string }
  | { type: "chat"; userId: string; playerName: string; text: string; timestamp: number }

// ---- Hook options ----

interface UsePokerWebSocketOptions {
  token: string | null
  roomId: string | null
  onRoomUpdate: (room: PublicRoomState) => void
  onAutoStartCountdown: (seconds: number) => void
  onAutoStartCancelled: () => void
  onGameStart: (handNumber: number, dealerIndex: number) => void
  onDealHoleCards: (cards: Card[]) => void
  onPlayerAction: (userId: string, action: string, amount: number) => void
  onCommunityCards: (cards: Card[], phase: Phase) => void
  onShowdown: (results: ShowdownResultPublic[]) => void
  onPotUpdate: (pot: number, sidePots: SidePot[]) => void
  onTurnChange: (userId: string, timeLimit: number) => void
  onGameEnd: (winners: { userId: string; amount: number }[]) => void
  onError: (message: string) => void
  onChat: (userId: string, playerName: string, text: string, timestamp: number) => void
  onReconnect: () => void
}

export function usePokerWebSocket({
  token,
  roomId,
  onRoomUpdate,
  onAutoStartCountdown,
  onAutoStartCancelled,
  onGameStart,
  onDealHoleCards,
  onPlayerAction,
  onCommunityCards,
  onShowdown,
  onPotUpdate,
  onTurnChange,
  onGameEnd,
  onError,
  onChat,
  onReconnect,
}: UsePokerWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null)
  const retriesRef = useRef(0)
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmountedRef = useRef(false)

  const cbRef = useRef({
    onRoomUpdate,
    onAutoStartCountdown,
    onAutoStartCancelled,
    onGameStart,
    onDealHoleCards,
    onPlayerAction,
    onCommunityCards,
    onShowdown,
    onPotUpdate,
    onTurnChange,
    onGameEnd,
    onError,
    onChat,
    onReconnect,
  })
  cbRef.current = {
    onRoomUpdate,
    onAutoStartCountdown,
    onAutoStartCancelled,
    onGameStart,
    onDealHoleCards,
    onPlayerAction,
    onCommunityCards,
    onShowdown,
    onPotUpdate,
    onTurnChange,
    onGameEnd,
    onError,
    onChat,
    onReconnect,
  }

  const sendAction = useCallback(
    (action: string, amount?: number) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({ type: "action", action, amount })
        )
      }
    },
    []
  )

  const sendChat = useCallback(
    (text: string) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({ type: "chat", text })
        )
      }
    },
    []
  )

  const connect = useCallback(() => {
    if (!token || !roomId) return
    if (unmountedRef.current) return

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:"
    const url = `${proto}//${window.location.host}/ws/poker?token=${encodeURIComponent(token)}&roomId=${encodeURIComponent(roomId)}`

    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      retriesRef.current = 0
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping")
        }
      }, 30_000)
    }

    ws.onmessage = (ev) => {
      if (ev.data === "pong") return

      let event: PokerWSEvent
      try {
        event = JSON.parse(ev.data)
      } catch {
        return
      }

      const cbs = cbRef.current

      switch (event.type) {
        case "room_update":
          cbs.onRoomUpdate(event.room)
          break
        case "auto_start_countdown":
          cbs.onAutoStartCountdown(event.seconds)
          break
        case "auto_start_cancelled":
          cbs.onAutoStartCancelled()
          break
        case "game_start":
          cbs.onGameStart(event.handNumber, event.dealerIndex)
          break
        case "deal_hole_cards":
          cbs.onDealHoleCards(event.cards)
          break
        case "player_action":
          cbs.onPlayerAction(event.userId, event.action, event.amount)
          break
        case "community_cards":
          cbs.onCommunityCards(event.cards, event.phase)
          break
        case "showdown":
          cbs.onShowdown(event.results)
          break
        case "pot_update":
          cbs.onPotUpdate(event.pot, event.sidePots)
          break
        case "turn_change":
          cbs.onTurnChange(event.userId, event.timeLimit)
          break
        case "game_end":
          cbs.onGameEnd(event.winners)
          break
        case "chat":
          cbs.onChat(event.userId, event.playerName, event.text, event.timestamp)
          break
        case "error":
          cbs.onError(event.message)
          break
      }
    }

    ws.onclose = () => {
      cleanup()
      if (unmountedRef.current) return

      const delay = Math.min(1000 * 2 ** retriesRef.current, 30_000)
      retriesRef.current++

      reconnectTimeoutRef.current = setTimeout(() => {
        cbRef.current.onReconnect()
        connect()
      }, delay)
    }

    ws.onerror = () => {
      // onclose will fire after onerror
    }
  }, [token, roomId])

  function cleanup() {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
  }

  useEffect(() => {
    unmountedRef.current = false
    connect()

    return () => {
      unmountedRef.current = true
      cleanup()
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect])

  return { sendAction, sendChat }
}
